"use client";

import { useFormContext, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useFormModeGuard } from "@/forms/shared/components/use-form-mode-guard";
import { z } from "zod";

export interface PhoneCountryOption {
  countryCode: string;
  countryName: string;
  dialCode: string;
}

interface PhoneCountryInputFieldProps {
  label: string;
  countryOptions: PhoneCountryOption[];
  phoneFieldName?: string;
  countryFieldName?: string;
  dialCodeFieldName?: string;
  nationalNumberFieldName?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const normalizeDialCode = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

const sanitizePhoneDigits = (value: string): string => value.replace(/\D/g, "");

const buildInternationalPhone = (dialCode: string, nationalNumber: string): string => {
  const normalizedDialCode = normalizeDialCode(dialCode);
  const digits = sanitizePhoneDigits(nationalNumber);
  if (!normalizedDialCode || !digits) return "";
  return `${normalizedDialCode}${digits}`;
};

const getDefaultCountryOption = (
  options: PhoneCountryOption[]
): PhoneCountryOption | null => {
  if (options.length === 0) return null;
  const spain = options.find((option) => option.countryCode === "ES");
  return spain || options[0];
};

const errorMessageSchema = z.object({ message: z.string().optional() });
const optionalStringSchema = z.string().optional();

const getErrorMessage = <TValue,>(error: TValue): string => {
  const parsed = errorMessageSchema.safeParse(error);
  return parsed.success ? parsed.data.message ?? "" : "";
};

export function PhoneCountryInputField({
  label,
  countryOptions,
  phoneFieldName = "phone",
  countryFieldName = "phone_country_code",
  dialCodeFieldName = "phone_dial_code",
  nationalNumberFieldName = "phone_national_number",
  required = false,
  disabled = false,
  placeholder = "600000000",
}: PhoneCountryInputFieldProps) {
  const { control, getValues, setValue, formState } = useFormContext();
  const { sideEffectsAllowed } = useFormModeGuard(disabled);

  const selectedCountryCodeResult = optionalStringSchema.safeParse(
    useWatch({ control, name: countryFieldName }),
  );
  const dialCodeResult = optionalStringSchema.safeParse(
    useWatch({ control, name: dialCodeFieldName }),
  );
  const selectedCountryCode = selectedCountryCodeResult.success
    ? selectedCountryCodeResult.data
    : undefined;
  const dialCode = dialCodeResult.success ? dialCodeResult.data : undefined;
  const selectedCountry = countryOptions.find(
    (option) => option.countryCode === selectedCountryCode
  );
  const defaultCountry = getDefaultCountryOption(countryOptions);
  const effectiveCountry = selectedCountry ?? defaultCountry;

  const syncCountryFields = (country: PhoneCountryOption, shouldDirty = false) => {
    setValue(countryFieldName, country.countryCode, {
      shouldValidate: false,
      shouldDirty,
    });
    setValue(`${countryFieldName}_label`, country.countryName, {
      shouldValidate: false,
      shouldDirty: false,
    });
    setValue(dialCodeFieldName, country.dialCode, {
      shouldValidate: false,
      shouldDirty: false,
    });
  };

  const syncPhoneField = (country: PhoneCountryOption, value: string) => {
    const digits = sanitizePhoneDigits(value);
    setValue(phoneFieldName, buildInternationalPhone(country.dialCode, digits), {
      shouldValidate: digits.length > 0,
      shouldDirty: false,
    });
  };

  return (
    <FormField
      control={control}
      name={nationalNumberFieldName}
      render={({ field, fieldState }) => {
        const effectiveDialCode = normalizeDialCode(
          selectedCountry?.dialCode || dialCode || defaultCountry?.dialCode || ""
        );
        const fieldErrorMessage = getErrorMessage(fieldState.error);
        const hiddenCountryErrorMessage = getErrorMessage(formState.errors[countryFieldName]);
        const hiddenDialCodeErrorMessage = getErrorMessage(formState.errors[dialCodeFieldName]);
        const hiddenPhoneErrorMessage = getErrorMessage(formState.errors[phoneFieldName]);
        const extraErrorMessage = fieldErrorMessage
          ? ""
          : hiddenCountryErrorMessage ||
            hiddenDialCodeErrorMessage ||
            hiddenPhoneErrorMessage;

        return (
          <FormItem className="flex flex-col">
            <FormLabel>
              {label} {required && "*"}
            </FormLabel>
            <InputGroup data-disabled={!sideEffectsAllowed ? true : undefined}>
              <InputGroupAddon align="inline-start" className="gap-1 pr-1">
                <Select
                  value={selectedCountryCode || ""}
                  onValueChange={(nextCountryCode) => {
                    if (!sideEffectsAllowed) return;
                    const nextCountry = countryOptions.find((option) => {
                      return option.countryCode === nextCountryCode;
                    });
                    if (!nextCountry) {
                      setValue(countryFieldName, nextCountryCode, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                      return;
                    }

                    syncCountryFields(nextCountry, true);

                    const digits = sanitizePhoneDigits(
                      String(getValues(nationalNumberFieldName) || "")
                    );
                    syncPhoneField(nextCountry, digits);
                  }}
                  disabled={!sideEffectsAllowed || countryOptions.length === 0}
                >
                  <SelectTrigger
                    size="sm"
                    aria-label="País del número de teléfono"
                    className={cn(
                      "data-[size=sm]:h-6 border-0 bg-transparent px-1 py-0 text-xs shadow-none focus-visible:border-transparent focus-visible:ring-0",
                      !sideEffectsAllowed && "cursor-text"
                    )}
                  >
                    <SelectValue placeholder="Selecciona país" />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    align="start"
                    className="max-h-[min(18rem,var(--radix-select-content-available-height))]"
                  >
                    {countryOptions.map((option) => (
                      <SelectItem key={option.countryCode} value={option.countryCode}>
                        {option.countryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputGroupText className="border-l pl-2 text-xs font-medium">
                  {effectiveDialCode || "+--"}
                </InputGroupText>
              </InputGroupAddon>
              <FormControl>
                <InputGroupInput
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder={placeholder}
                  readOnly={!sideEffectsAllowed}
                  className={cn(
                    !sideEffectsAllowed && "bg-muted opacity-50 cursor-text select-text"
                  )}
                  value={field.value ?? ""}
                  onChange={(event) => {
                    if (!sideEffectsAllowed) return;
                    const sanitizedNumber = sanitizePhoneDigits(event.target.value);
                    field.onChange(sanitizedNumber);
                    if (effectiveCountry) {
                      syncCountryFields(effectiveCountry);
                      syncPhoneField(effectiveCountry, sanitizedNumber);
                    }
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
            </InputGroup>
            <FormMessage />
            {extraErrorMessage ? (
              <p className="text-sm text-destructive">{extraErrorMessage}</p>
            ) : null}
          </FormItem>
        );
      }}
    />
  );
}
